/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo } from "react";
import { ListRange, Virtuoso } from "react-virtuoso";
import { styled, zIndexes } from "@iventis/styles";
import { Theme } from "@emotion/react";

import { useStateRenderOnCondition } from "@iventis/utilities";
import { TreeBrowserNode } from "../types/data-types";
import { VirtualScrollingComponentProps } from "./virtual-scrolling-component";
import { FlatNode, getFixedNode } from "../helpers/virtual-scrolling-helpers";

interface VirtualScrollingStickyHeaderProps<TNode extends TreeBrowserNode, TExtraProps extends any = undefined> extends VirtualScrollingComponentProps<TNode, TExtraProps> {
    headerNodeIds: string[];
}

/** Virtual scrolling for TreeBrowser with fixed header of a parent node for it's children nodes */
export const VirtualScrollingWithHeaderComponent = <TNode extends TreeBrowserNode, TExtraProps extends any = undefined>({
    handleHeightChange,
    virtuosoRef,
    nodeFlatTree,
    createFooter,
    components,
    extraProps,
    getInteractivity,
    followOutput,
    headerNodeIds,
}: VirtualScrollingStickyHeaderProps<TNode, TExtraProps>) => {
    // Contains the top and bottom index values of what nodes are being shown
    const [indexesOfNodesBeingShown, handleRangeChanged] = useStateRenderOnCondition<ListRange>(headerNodeIds.length > 0);

    const headerNode = useMemo(() => getFixedNode(headerNodeIds, nodeFlatTree, indexesOfNodesBeingShown), [headerNodeIds, nodeFlatTree, indexesOfNodesBeingShown]);

    // Amount of nodes we want to stick to the top
    const topItemCount = headerNode ? 1 : 0;

    /** Creates a node with optional container */
    const createNode = useCallback((nodeData: FlatNode<TNode>, containerPosition?: string) => {
        const NodeComponent = components[nodeData.type];
        const node = (
            <NodeComponent key={nodeData.id} node={nodeData} expanded={false} extraProps={extraProps} getInteractivity={getInteractivity} parentNodeType={nodeData.parentType} />
        );
        return containerPosition ? <HeaderNodeContainer position={containerPosition}>{node}</HeaderNodeContainer> : node;
    }, []);

    const topItem = useCallback(({ context }) => (context.stickyNode == null ? createNode(context.topNode, "static") : createNode(context.stickyNode, "sticky")), []);

    return (
        <Virtuoso
            ref={virtuosoRef}
            totalListHeightChanged={handleHeightChange}
            style={{ height: "100%" }}
            data={nodeFlatTree}
            topItemCount={topItemCount}
            context={{ stickyNode: headerNode, topNode: nodeFlatTree[0] }}
            rangeChanged={handleRangeChanged}
            components={{
                Footer: createFooter,
                // Bug with Virtuoso which causes the top node to be not show after top item list component is used for node header
                TopItemList: topItem,
            }}
            itemContent={(_, data) => createNode(data)}
            followOutput={followOutput}
        />
    );
};

const HeaderNodeContainer = styled.div<{ position: string }>`
    width: 100%;
    top: 0px;
    z-index: ${zIndexes.mapSidebar.stickyNode};
    margin-top: 0px;
    background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    position: ${({ position }) => position};
`;
